"use client";

import { SerializedFunnelData } from "@/hooks/funnelPersistence";
import { getTodayAtMidnight, createProductVariant, ProductVariant } from "@/utils/funnel";

export interface HydratedValues {
  contractId: string | null;
  contractToken: string | null;
  offerAgentId: string | null;
  petType: "cat" | "dog" | null;
  petName: string | null;
  breed: string | null;
  breedLabel: string | null;
  birthDate: Date | undefined;
  selectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  gender: "male" | "female" | "unknown" | null;
  isCastrated: boolean;
  isHouseCat: boolean;
  selectedDogSize: "small" | "large" | null;
  chipNumber: string | null;
  bundlePrices: { S: number; M: number; L: number } | null;
  selectedProduct: ProductVariant | null;
  finalPrice: number | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  street: string | null;
  housenumber: string | null;
  city: string | null;
  zipCode: string | null;
  country: string | null;
  contactBirthdate: Date | undefined;
  customerGender: "male" | "female" | null;
  preexistingCoverage: boolean;
  worldwideCoverage: boolean;
  yearlyDeductible: number;
  coPayCoveragePercentage: number;
  paymentInterval: 1 | 2 | 4 | 12;
  paymentMethod: "credit_card" | "ESR";
  startOfInsurance: Date;
  firstDue: Date | null;
  isOfferLoadingFromUrl: boolean;
  isHydrated: boolean;
  dispatchContract: "customer" | "customer_and_agent" | "agent";
  dispatchInvoice: "customer" | "customer_and_agent" | "agent";
  dispatchReminder: "customer" | "customer_and_agent" | "agent";
  dispatchEmail: "customer" | "customer_and_agent" | "agent";
  agentDispatchEmail: string | null;
  agentId: string | null;
  whitelabelPartner: string | null;
  offerLoadedFromUrl: boolean;
  hasLeftConfirmationAfterOfferLoad: boolean;
  consultationProtocolDocumentId: number | null;
  consultationProtocolFilename: string | null;
  consultationProtocolPendingFile: string | null;
  consultationProtocolPendingFilename: string | null;
}

export const getDefaultValues = (options?: { defaultDispatchEmail?: "customer" | "customer_and_agent" | "agent" }): HydratedValues => {
  const defaultDispatchEmail = options?.defaultDispatchEmail ?? "customer";
  return {
    contractId: null,
    contractToken: null,
    offerAgentId: null,
    petType: null,
    petName: null,
    breed: null,
    breedLabel: null,
    birthDate: undefined,
    selectedBreedType: null,
    gender: null,
    isCastrated: false,
    isHouseCat: false,
    selectedDogSize: null,
    chipNumber: null,
    bundlePrices: null,
    selectedProduct: null,
    finalPrice: null,
    contactFirstName: null,
    contactLastName: null,
    contactEmail: null,
    contactPhone: null,
    street: null,
    housenumber: null,
    city: null,
    zipCode: null,
    country: null,
    contactBirthdate: undefined,
    customerGender: null,
    preexistingCoverage: false,
    worldwideCoverage: false,
    yearlyDeductible: 300,
    coPayCoveragePercentage: 80,
    paymentInterval: 12,
    paymentMethod: "credit_card",
    startOfInsurance: getTodayAtMidnight(),
    firstDue: null,
    isOfferLoadingFromUrl: false,
    isHydrated: false,
    dispatchContract: "customer",
    dispatchInvoice: "customer",
    dispatchReminder: "customer",
    dispatchEmail: defaultDispatchEmail,
    agentDispatchEmail: null,
    agentId: null,
    whitelabelPartner: null,
    offerLoadedFromUrl: false,
    hasLeftConfirmationAfterOfferLoad: false,
    consultationProtocolDocumentId: null,
    consultationProtocolFilename: null,
    consultationProtocolPendingFile: null,
    consultationProtocolPendingFilename: null,
  };
};

export const hydrateValuesFromStorage = (
  data: SerializedFunnelData,
  defaultDispatchEmail: "customer" | "customer_and_agent" | "agent" = "customer"
): HydratedValues => {
  const defaults = getDefaultValues({ defaultDispatchEmail });
  const parseOptionalDate = (value?: string | null): Date | null => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const selectedProduct =
    data.selectedProductId ? createProductVariant(data.selectedProductId as "s" | "m" | "l") : null;

  // Handle backward compatibility for contactName
  let hydratedFirstName = data.contactFirstName ?? defaults.contactFirstName;
  let hydratedLastName = data.contactLastName ?? defaults.contactLastName;

  if (data.contactName && !data.contactFirstName && !data.contactLastName) {
    const parts = data.contactName.trim().split(/\s+/);
    hydratedFirstName = parts[0] || null;
    hydratedLastName = parts.slice(1).join(" ") || null;
  }

  return {
    contractId: data.contractId ?? defaults.contractId,
    contractToken: data.contractToken ?? defaults.contractToken,
    offerAgentId: data.offerAgentId ?? defaults.offerAgentId,
    petType: data.petType ?? defaults.petType,
    petName: data.petName ?? defaults.petName,
    breed: data.breed ?? defaults.breed,
    breedLabel: data.breedLabel ?? defaults.breedLabel,
    birthDate: data.birthDate ? new Date(data.birthDate) : defaults.birthDate,
    selectedBreedType: data.selectedBreedType ?? defaults.selectedBreedType,
    gender: data.gender ?? defaults.gender,
    isCastrated: !!data.isCastrated,
    isHouseCat: !!data.isHouseCat,
    selectedDogSize: data.selectedDogSize ?? defaults.selectedDogSize,
    chipNumber: data.chipNumber ?? defaults.chipNumber,
    bundlePrices: data.bundlePrices ?? defaults.bundlePrices,
    selectedProduct,
    finalPrice: data.finalPrice ?? defaults.finalPrice,
    contactFirstName: hydratedFirstName,
    contactLastName: hydratedLastName,
    contactEmail: data.contactEmail ?? defaults.contactEmail,
    contactPhone: data.contactPhone ?? defaults.contactPhone,
    street: data.street ?? defaults.street,
    housenumber: data.housenumber ?? defaults.housenumber,
    city: data.city ?? defaults.city,
    zipCode: data.zipCode ?? defaults.zipCode,
    country: data.country ?? defaults.country,
    contactBirthdate: data.contactBirthdate ? new Date(data.contactBirthdate) : defaults.contactBirthdate,
    customerGender: data.customerGender ?? defaults.customerGender,
    preexistingCoverage: !!data.preexistingCoverage,
    worldwideCoverage: !!data.worldwideCoverage,
    yearlyDeductible: data.yearlyDeductible ?? defaults.yearlyDeductible,
    coPayCoveragePercentage: data.coPayCoveragePercentage ?? defaults.coPayCoveragePercentage,
    paymentInterval: data.paymentInterval ?? defaults.paymentInterval,
    paymentMethod: data.paymentMethod ?? defaults.paymentMethod,
    startOfInsurance: data.startOfInsurance ? new Date(data.startOfInsurance) : defaults.startOfInsurance,
    firstDue: parseOptionalDate(data.firstDue) ?? defaults.firstDue,
    isOfferLoadingFromUrl: !!data.isOfferLoadingFromUrl,
    isHydrated: true, // Always set to true after hydration attempt
    dispatchContract: data.dispatchContract ?? defaults.dispatchContract,
    dispatchInvoice: data.dispatchInvoice ?? defaults.dispatchInvoice,
    dispatchReminder: data.dispatchReminder ?? defaults.dispatchReminder,
    dispatchEmail: data.dispatchEmail ?? defaults.dispatchEmail,
    agentDispatchEmail: data.agentDispatchEmail ?? defaults.agentDispatchEmail,
    agentId: data.agentId ?? defaults.agentId,
    whitelabelPartner: data.whitelabelPartner ?? defaults.whitelabelPartner,
    offerLoadedFromUrl: !!data.offerLoadedFromUrl,
    hasLeftConfirmationAfterOfferLoad: !!data.hasLeftConfirmationAfterOfferLoad,
    consultationProtocolDocumentId: data.consultationProtocolDocumentId ?? defaults.consultationProtocolDocumentId,
    consultationProtocolFilename: data.consultationProtocolFilename ?? defaults.consultationProtocolFilename,
    consultationProtocolPendingFile: data.consultationProtocolPendingFile ?? defaults.consultationProtocolPendingFile,
    consultationProtocolPendingFilename: data.consultationProtocolPendingFilename ?? defaults.consultationProtocolPendingFilename,
  };
};
